import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";

export default function Waiver() {
  useSEO({
    title: "Liability Waiver & Release — The Flow State Method LLC",
    description: "Read the full Liability Waiver & Release for The Flow State Method LLC. Assumption of risk, release of liability, indemnification, and virtual-session acknowledgments for boxing coaching.",
    path: "/waiver",
  });
  return (
    <div className="App relative">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="waiver-page">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Legal</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight mb-2">Liability Waiver & Release</h1>
        <p className="text-zinc-500 text-sm">Effective February 3, 2026 · The Flow State Method LLC</p>

        <div className="space-y-6 text-sm text-zinc-300 leading-relaxed">

          <Section title="Parties">
            This Waiver, Release of Liability, Assumption of Risk, and Indemnity Agreement ("Waiver") is entered into between
            you ("Participant", "I", "me") and <strong>The Flow State Method LLC</strong>, its owner Coach Christopher, employees,
            independent contractors, agents, and assigns (collectively, the "Released Parties").
          </Section>

          <Section title="1. Assumption of Risk — Boxing Is Inherently Dangerous">
            I understand and acknowledge that boxing instruction — including conditioning, footwork drills, shadowboxing,
            heavy bag work, pad work, partner drills, defensive movement, and any physical activity that may occur during a
            session — is inherently dangerous and involves risk of serious bodily injury, including but not limited to:
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Sprains, strains, fractures, dislocations, contusions, lacerations, cartilage damage, and joint damage;</li>
              <li>Hand, wrist, shoulder, knee, hip, ankle, and back injuries;</li>
              <li>Concussion, head trauma, neck injury, dental injury, or eye injury;</li>
              <li>Cardiac events, dehydration, heat exhaustion, hypoglycemia, fainting;</li>
              <li>Aggravation of pre-existing conditions;</li>
              <li>Injuries from falls, equipment failure, training-space hazards, partner contact, or my own actions;</li>
              <li>For virtual sessions: injuries from training in an unsupervised environment, including collision with objects, slipping, or improper use of equipment;</li>
              <li>Permanent disability or death.</li>
            </ul>
            I voluntarily assume all such risks, known and unknown, even if arising from the ordinary negligence of the Released Parties, and I assume full responsibility for any resulting injury, illness, damage, loss, claim, liability, or expense.
          </Section>

          <Section title="2. Release of Liability">
            In consideration of being permitted to participate, I HEREBY RELEASE, WAIVE, DISCHARGE, AND COVENANT NOT TO SUE the Released Parties from any and all liability, claims, demands, actions, and causes of action whatsoever arising out of or related to any loss, damage, injury, illness, or death I may sustain while participating in the Services, including any loss or damage caused by the ORDINARY NEGLIGENCE of the Released Parties. This release does not extend to gross negligence, recklessness, or intentional misconduct where prohibited by law.
          </Section>

          <Section title="3. Indemnification">
            I AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS the Released Parties from any loss, liability, damage, or cost (including reasonable attorneys' fees) they may incur due to my participation, including claims brought by my family, heirs, assigns, or any third party (including any guest or minor I bring or train alongside).
          </Section>

          <Section title="4. Medical Fitness & Disclosure">
            I represent that I am in good physical condition and have no medical condition, injury, pregnancy, medication, or impairment that would make participation in boxing instruction unsafe. If I have any such condition, I have consulted my physician and obtained clearance to participate. I agree to disclose such conditions to the coach before training and immediately notify the coach of any pain, dizziness, lightheadedness, chest discomfort, shortness of breath, or other symptom during a session. I authorize the Released Parties, in the event of an emergency, to call 911 and to administer or arrange emergency medical care at my expense.
          </Section>

          <Section title="5. Live Virtual Session — Specific Acknowledgments">
            All sessions are conducted via Zoom. I specifically acknowledge:
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>The coach is not physically present and cannot prevent injuries, intervene, spot me, or provide first aid;</li>
              <li>I am solely responsible for selecting a safe training space free of trip hazards, fragile items, glass, sharp corners, and other people not participating;</li>
              <li>I am solely responsible for the condition and proper use of any equipment I use, including gloves, wraps, heavy bags, mats, weights, jump ropes, and footwear;</li>
              <li>I will stop training immediately if I feel unsafe or unable to continue;</li>
              <li>The Released Parties are not responsible for any property damage in my home or training space.</li>
            </ul>
          </Section>

          <Section title="6. Equipment & Property">
            I am solely responsible for any equipment, mats, gloves, wraps, bags, or other gear I use during a session, including its condition and proper use. The Released Parties are not liable for any property damage to my home, training space, or equipment occurring during or as a result of a session.
          </Section>

          <Section title="7. Photo / Video / Likeness">
            I grant the Released Parties permission to capture anonymous, non-identifying photo and video during sessions (e.g., wide shots, equipment, action without faces) and use them for promotion, social media, and training content. Use of any identifiable image requires my separate written consent.
          </Section>

          <Section title="8. Minor Participants">
            If the Participant is under 18, the parent or legal guardian signing this Waiver: (a) confirms they have legal authority to do so; (b) accepts all risks on behalf of the minor; (c) agrees this Waiver is binding on the minor, the parent, the minor's heirs, and the family; (d) agrees to be present or to provide adult supervision as required by the coach.
          </Section>

          <Section title="9. Severability & Survival">
            If any provision of this Waiver is found unenforceable, the remaining provisions remain in full force. This Waiver survives termination of services and applies to all past, current, and future sessions until expressly revoked in writing.
          </Section>

          <Section title="10. Governing Law & Venue">
            This Waiver is governed by the laws of the State of New Jersey. Venue for any action permitted under this Waiver lies in Somerset County, NJ.
          </Section>

          <Section title="11. Acknowledgment">
            <strong className="text-white uppercase">I have read this Waiver in full. I understand it. I am at least 18 years old (or a parent/guardian of a participating minor). I am signing it freely and voluntarily. I understand I am giving up substantial legal rights, including the right to sue.</strong>
          </Section>

          <Section title="Electronic Acceptance">
            Acceptance occurs when you check the "I accept the Waiver" box at checkout. The Company records the timestamp,
            account email, IP address, and browser user-agent at the moment of acceptance as evidence of consent.
            This electronic signature carries the same legal effect as a handwritten signature.
          </Section>

        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display uppercase tracking-widest text-sm text-white mb-2">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
